import type { ImpressaoConfigResponse } from "@/types/impressao-config-response";

// Catálogo das tratativas de gravação do módulo de impressão.
//
// "Tratativa" é cada verificação extra que o firmware faz em volta da
// gravação — conferir se a tag já estava gravada, corrigir uma tag gravada
// pela metade, blacklistar a virgem consumida, etc. Historicamente todas
// eram fixas no firmware; agora cada uma pode ser desligada por configuração
// de impressão, e o estado desejado viaja num campo extra do pacote
// (`TRAT>jg=1;pm=0;...`).
//
// Regras de compatibilidade que sustentam esse desenho:
//   - o campo vai no FIM do pacote e é achado pelo firmware por prefixo, não
//     por posição; firmware antigo simplesmente ignora o token a mais;
//   - toda tratativa nasce LIGADA, que é o comportamento histórico. Pacote
//     sem o token = tudo ligado;
//   - o firmware anuncia em `CAPTRAT>` (na resposta do comando ST) quais
//     chaves sabe controlar. Sem esse campo, o site não libera os seletores.
//
// Mudar uma `chave` aqui quebra o contrato com o firmware — os valores são
// estáveis de propósito.

export type ChaveTratativa =
  "jg" | "pm" | "cf" | "cm" | "fp" | "pv" | "bl" | "ap";

/** Campos de ImpressaoConfigResponse que guardam o estado das tratativas. */
export type CampoTratativa =
  | "tratativa_detectar_ja_gravada"
  | "tratativa_gravada_pela_metade"
  | "conferir_tag_apos_gravar"
  | "tratativa_epc_corrompido_miolo"
  | "tratativa_falso_positivo_erro"
  | "tratativa_proxima_tag_virgem"
  | "tratativa_blacklist"
  | "tratativa_aumento_potencia";

export type Tratativa = {
  /** Chave usada no protocolo com o módulo. Não mudar. */
  chave: ChaveTratativa;
  /** Campo correspondente na configuração de impressão. */
  campo: CampoTratativa;
  titulo: string;
  descricao: string;
};

export const TRATATIVAS: Tratativa[] = [
  {
    chave: "jg",
    campo: "tratativa_detectar_ja_gravada",
    titulo: "Detectar etiqueta já gravada",
    descricao:
      "Antes de gravar, lê o campo e, se o EPC esperado já estiver na etiqueta, considera gravada sem regravar.",
  },
  {
    chave: "pm",
    campo: "tratativa_gravada_pela_metade",
    titulo: "Corrigir etiqueta gravada pela metade",
    descricao:
      "Procura etiqueta com o começo do EPC novo e o fim ainda virgem (gravação interrompida) e regrava por cima.",
  },
  {
    chave: "cf",
    campo: "conferir_tag_apos_gravar",
    titulo: "Conferir gravação por leitura",
    descricao:
      "Depois do módulo reportar sucesso, lê a etiqueta para confirmar que o EPC no campo é o esperado.",
  },
  {
    chave: "cm",
    campo: "tratativa_epc_corrompido_miolo",
    titulo: "Corrigir EPC corrompido no miolo",
    descricao:
      "Detecta etiqueta com pontas certas e meio errado (gravação parcial) e regrava. Depende da conferência por leitura.",
  },
  {
    chave: "fp",
    campo: "tratativa_falso_positivo_erro",
    titulo: "Confirmar erro de gravação",
    descricao:
      "Quando o módulo reporta falha, lê o campo antes de devolver erro — o módulo às vezes acusa falha depois de gravar.",
  },
  {
    chave: "pv",
    campo: "tratativa_proxima_tag_virgem",
    titulo: "Antecipar próxima etiqueta virgem",
    descricao:
      "Calcula qual deve ser a próxima virgem do rolo e, se ela já estiver no campo, usa como filtro.",
  },
  {
    chave: "bl",
    campo: "tratativa_blacklist",
    titulo: "Descartar etiquetas já usadas (blacklist)",
    descricao:
      "Guarda no módulo os EPCs já consumidos para que não sejam escolhidos de novo nas próximas gravações.",
  },
  {
    chave: "ap",
    campo: "tratativa_aumento_potencia",
    titulo: "Aumentar potência nas retentativas",
    descricao:
      "A cada retentativa de gravação sobe a potência (até 3 degraus) e devolve à potência original no fim.",
  },
];

/** Prefixo do campo que o firmware publica na resposta do ST com as chaves
 *  que ele sabe controlar. */
export const PREFIXO_CAPACIDADES = "CAPTRAT>";

/** Versão do firmware em que as tratativas passaram a ser configuráveis.
 *  Só informativo — quem manda é o `CAPTRAT>`. */
export const VERSAO_MINIMA_TRATATIVAS = "2.26";

/**
 * Lê as chaves suportadas na resposta do comando ST (`INFO|...|CAPTRAT>jg,pm`).
 * Procura por prefixo porque a posição do campo muda entre as variantes do
 * firmware (a de Ethernet tem campos a mais).
 * Retorna `[]` quando o firmware não anuncia nada — é como um firmware antigo
 * se apresenta.
 */
export function lerCapacidadesTratativas(
  partesDoInfo: string[],
): ChaveTratativa[] {
  const campo = partesDoInfo.find((parte) =>
    parte.trim().startsWith(PREFIXO_CAPACIDADES),
  );
  if (!campo) return [];
  return campo
    .trim()
    .substring(PREFIXO_CAPACIDADES.length)
    .split(",")
    .map((chave) => chave.trim())
    .filter((chave): chave is ChaveTratativa =>
      TRATATIVAS.some((t) => t.chave === chave),
    );
}

/** Estado efetivo de uma tratativa. Campo ausente/indefinido = ligada, que é
 *  o comportamento histórico do firmware. */
export function tratativaAtiva(
  config: Partial<ImpressaoConfigResponse> | null | undefined,
  campo: CampoTratativa,
): boolean {
  return config?.[campo] !== false;
}

/**
 * Monta o campo extra do pacote de impressão (`TRAT>jg=1;pm=0;...`).
 * Sempre manda todas as chaves: assim o firmware não precisa distinguir
 * "chave omitida" de "chave desligada".
 */
export function serializarTratativas(
  config: Partial<ImpressaoConfigResponse> | null | undefined,
): string {
  const pares = TRATATIVAS.map(
    (t) => `${t.chave}=${tratativaAtiva(config, t.campo) ? "1" : "0"}`,
  );
  return `TRAT>${pares.join(";")}`;
}
